import { Component, OnInit, Input, AfterViewInit } from '@angular/core';
import { Sample } from 'src/app/shared/models/sample.model';
import { PrintToggle } from '../enums/print-toggle.enum';
import { CurrentOrderService } from '../services/current-order.service';
import { AuthConfigService, StoreService, TranslationService } from 'projects/dop-core/src/public-api';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { AdditionalFieldHelper } from '../services/additional-field-helper.service';
import { AdditionalField } from '../models/additional-field.model';
import { OrderDataService } from '../data-service/order-data.service';
import { PriorityLevel } from '../models/priority-level.model';
import { OrderDetailsMapperService } from '../services/order-details-mapper.service';
import { AttachDocument } from '../models/attach-document';
import { DownloadFileHelperService } from '../services/download-file-helper.service';
import { ScanStatus } from '../enums/scan-status.enum';
import { FileStatus } from '../enums/file-status.enum';
import { AttachDocumentService } from '../data-service/attach-document.service';
import { File } from 'src/app/shared/models/attach-document/file.model';
import { LinkedObjectType } from '../enums/linked-object-type.enum';
import { StringFormatter } from '../pipes/string-formatter.pipe';
import { throwError } from 'rxjs/internal/observable/throwError';
import { StandardComment } from '../models/standard-comment.model';
import { BaseComponent } from 'src/app/features/guided-order/base.component';
import { BrowserFinder } from '../extensions/browser.finder';
import { ReportGrouping } from '../enums/report-grouping.enum';
import { FormatterHelperService } from '../services/formatter-helper.service';
import { AddOrderDetailService } from 'src/app/features/guided-order/order-n-ship/add-order-detail/add-order-detail.service';
import AnalysisHelper from 'projects/dop-ordering/src/lib/features/dop-analysis/dop-analysis.helper';
import { AttachDocumentHelperService } from '../services/attach-document-helper.service';

@Component({
  selector: 'app-sample-grid-preview',
  templateUrl: './sample-grid-preview.component.html',
  styleUrls: ['./sample-grid-preview.component.scss']
})
export class SampleGridPreviewComponent extends BaseComponent implements OnInit, AfterViewInit {

  @Input() printing: PrintToggle;
  @Input() isViewSchedule: boolean = false;

  cols: any[];
  samples: Sample[] = [];
  printToggle = PrintToggle;
  packingListCols: any[];
  showPrice = false;
  downloadUrl: string;
  displayStructuredComment = false;
  structuredCommentValues: StandardComment[] = [];
  structuredCommentHeader: string;
  isChrome = false;
  AnalysisHelper = AnalysisHelper;
  isExternalLIMS = false;

  get CurrentOrder() {
    return this.currentOrderService.currentOrder;
  }

  constructor(
    currentOrderService: CurrentOrderService,
    private storeService: StoreService,
    private additionalFieldHelper: AdditionalFieldHelper,
    private orderDataService: OrderDataService,
    private orderDetailsMapperService: OrderDetailsMapperService,
    private downloadFileHelperService: DownloadFileHelperService,
    private authConfigService: AuthConfigService,
    private attachDocumentService: AttachDocumentService,
    private formatterHelperService: FormatterHelperService,
    private addOrderDetailService: AddOrderDetailService,
    private attachDocumentHelperService: AttachDocumentHelperService
  ) {
    super(currentOrderService);
  }


  async ngOnInit(): Promise<void> {
    this.isExternalLIMS = await this.IsExternalLimsValue();
    this.isChrome = BrowserFinder.isChrome();
    if (this.CurrentOrder.Status === 'Pending') {
      await this.getFullPendingOrder(this.CurrentOrder.OrderCode).then(_ => {
        this.samples.forEach(y => {
          y.analyticalReportGroupingRule = this.CurrentOrder.samples.find(f => f.sampleCode === y.sampleCode)?.analyticalReportGroupingRule;
          y.mandatoryAnalysisDisplay = y.analysisDisplay?.filter(z => z.isMrt);
          y.analysisDisplay = y.analysisDisplay?.filter(a => !a.isMrt);
        });
        this.getPriorityLevels().then(() => {
          this.CurrentOrder.ActiveIndex = 2;
          this.samples.forEach(y => {
            y.price = this.CurrentOrder.samples.filter(f => f.sampleCode === y.sampleCode).shift()?.price;
            y.attachDocuments = this.CurrentOrder.samples.find(f => f.sampleCode === y.sampleCode)?.attachDocuments;
          });
        });
      });
    } else {
      this.samples = this.CurrentOrder.samples;
      this.samples.forEach(y => {
        if (y.priorityLevel) {
          y.analyticalReportGroupingRule = ReportGrouping.SAMPLE;
        }
        y.mandatoryAnalysisDisplay = y.analysisDisplay?.filter(z => z.isMrt);
        y.analysisDisplay = y.analysisDisplay?.filter(a => !a.isMrt);
      });
      this.getPriorityLevels().then(() => {
        this.CurrentOrder.ActiveIndex = 2;
      });
    }

    this.showPrice = this.currentOrderService.currentOrder.displayPrice;

    if (this.CurrentOrder.orderTemplate.structuredComment && this.CurrentOrder.orderTemplate.structuredComment.toLowerCase() !== 'none') {
      this.displayStructuredComment = true;
      this.structuredCommentHeader = this.CurrentOrder.standardCommentTemplate ? this.CurrentOrder.standardCommentTemplate.templateName
        : this.CurrentOrder.commentTemplate;
    }

    this.cols = [
      { field: 'printBarcodeSelector', header: TranslationService.translate('vbeiPrintBarcodeSelector'), width: '160px' },
      { field: 'sampleDetail', header: TranslationService.translate('vbeiSampleDetails'), width: '15vw' },
      { field: 'analyses', header: TranslationService.translate('vbeiSelectedAnalyses'), width: this.showPrice ? '25vw' : '30vw' }
    ];

    if(this.currentOrder.orderTemplate.useMultipleDetermination)
    {
      this.cols.push({ field: 'TestCount', header: '', width: '110px' });
    }


    if (this.displayStructuredComment) {
      this.cols.push({ field: 'structuredComment', header: this.structuredCommentHeader, width: '95px' });
    }

    this.showPrice ?
      this.cols.push({ field: 'total', header: TranslationService.translate('vbeiTotal'), width: '100px' },
      { field: 'barcode', header: TranslationService.translate('vbeiBarcode'), width: '228px' }) :
      this.cols.push({ field: 'barcode', header: TranslationService.translate('vbeiBarcode'), width: '228px' });

    if(this.isViewSchedule) {
      this.cols = this.cols.filter(x=> ['barcode','printBarcodeSelector'].includes(x.field) === false);
    }

    this.packingListCols = [
      { field: 'customerReference', header: TranslationService.translate('vbeiSampleNameView') },
      { field: 'comment', header: TranslationService.translate('vbeiComments') }
    ];
    if(await this.IsExternalLimsValue()){
      this.packingListCols = [...this.packingListCols, { field: 'lotNumber', header: TranslationService.translate('vbeiLotNumber') }]
    }
    this.packingListCols = [...this.packingListCols,  { field: 'servingSize', header: TranslationService.translate('vbeiServingSize') },
    { field: 'priorityLevelDisplayName', header: TranslationService.translate('vbeiPriority') },
    { field: 'location', header: TranslationService.translate('vbeiDocumentsLocation') },
    { field: 'barcode', header: TranslationService.translate('vbeiBarcode'), width: '240px' }]

    this.downloadUrl = this.authConfigService.authConfig.redirectUri + 'downloadFile?internalFileName=';

    console.log(this.currentOrder)
  }

  ngAfterViewInit(): void {
    this.currentOrderService.analysesRemovedEvent.subscribe(() => {
      this.samples = this.currentOrderService.currentOrder.samples;
      this.samples?.forEach(sample => {
        if (sample.analyses) {
          const price = sample.analyses.reduce((acc, y) => acc + y.price, 0);
          const currencyCode = this.currentOrder.quotation.currencyCode + ' ';
          sample.price = this.formatterHelperService.formatCurrency(price, currencyCode);
          sample.priceValue = price;
        }
      });
    }
    )
  }

  getServingUnit(rowData: Sample) {
    if (rowData.servingSize && rowData.servingSize !== 'undefined') {
      return rowData.servingSize + ' ' + rowData.servingUnitNumeratorName + TranslationService.translate('vbeiServing');
    }
  }

  ColSpan()
  {
    if(this.displayStructuredComment && this.currentOrder.orderTemplate.useMultipleDetermination)
      return 3
    else if(this.displayStructuredComment || this.currentOrder.orderTemplate.useMultipleDetermination)
      return 2
    else
      return 1
  }

  private getPriorityLevels(): Promise<void> {
    return this.storeService.Get<any>(StoreKey.PriorityLevels).toPromise().then(data => {
      if (data) {
        this.assignPriorityLevelNames(data);
      } else {
        this.orderDataService.getPriorityLevels().toPromise().then(x => {
          this.assignPriorityLevelNames(x);
        });
      }
    });
  }

  private async IsExternalLimsValue()
  {
    return await this.currentOrderService.isExternalLIMS();
  }

  private assignPriorityLevelNames(data: PriorityLevel[]) {
    let priorityAtOrderLevel;
    if (this.addOrderDetailService.displayPriorityLevels) {
      priorityAtOrderLevel = data.find(y =>  y.code == this.addOrderDetailService.getSelectedPriority());
    }

    for (const z of this.samples) {
      if (this.CurrentOrder.Status === 'Submitted' && !z.priorityLevel) {
        continue;
      }
      
      const priority = data.find(y => [z.priorityLevel, priorityAtOrderLevel?.code].includes(y.code));
      z.priorityLevelDisplayName = priority?.name;
    }
  }

  shouldDisplayChild(child: any): boolean {
    const isMultipleDetermination = this.currentOrder.orderTemplate?.useMultipleDetermination;

    switch (true) {
      case child.subgroupCode !== undefined:
        // SubGrp
        return false;

      case child.analysisType === 0:
        // CP
        return isMultipleDetermination;

      case child.groupName !== undefined && child.childDisplay.length === 0:
        // CP child
        return false;

      case child.groupName === undefined && child.childDisplay.length > 0:
        // FreeQLPkg
        return isMultipleDetermination;

      case child.analysisType === 5 && child.executionNumber === undefined:
        // FreeQL Test inside FreeQLPkg
        return false;

      default:
        return isMultipleDetermination;
    }
  }


  printBarcodeSelector(event: Event) { }

  private async getFullPendingOrder(orderCode: string): Promise<void> {
    return await this.orderDataService.getFullPendingOrder(orderCode).toPromise().then(async x => {
      const attachments = await this.getOrderAttachments(x.order.orderDetails.orderReference, () => { });
      if (attachments) {
        this.mapOrderAttachments(attachments.filter(y => y.linkedObjectType === LinkedObjectType.Order));
        this.mapSampleAttachments(attachments.filter(y => y.linkedObjectType === LinkedObjectType.Sample));
      }
      
      this.samples = this.orderDetailsMapperService.mapSampleDetails(x.order.samples, x.order.orderDetails.currencyCode,
        x.order.quotationContract.receivingLabAddress.city,
        attachments ? attachments.filter(y => y.linkedObjectType === LinkedObjectType.Sample) : []);
    }).catch(() => { });
  }

  async getOrderAttachments(orderId: string, callback: () => void) {
    let files: File[] = [];
    await this.attachDocumentService.getAttachments(orderId)
      .toPromise()
      .then(res => {
        files = res;
      })
      .catch((error) => {
        if (error.status !== 500) {
          callback();
        } else {
          return throwError(error);
        }
      });

    callback();
    return files;
  }

  downloadAttachment(attachDocument: AttachDocument) {
    return this.downloadFileHelperService.downloadAttachment(attachDocument, () => { });
  }

  clean(file: AttachDocument): boolean {
    return file.status === ScanStatus.Clean;
  }

  noClean(sample: Sample) {
    return sample.attachDocuments?.filter(x => x.status === ScanStatus.Clean).length === 0;
  }
  attachments(sample: Sample): AttachDocument[] {
    return sample.attachDocuments?.filter(x => x.status !== ScanStatus.Infected
      && x.fileStatus !== FileStatus.Archived && x.fileStatus !== FileStatus.ArchivedDeleted);
  }

  hasAttachments(sample: Sample): boolean {
    return sample.attachDocuments?.filter(x => x.status !== ScanStatus.Infected).length > 0;
  }

  getArchivedFilesCount(sample: Sample): number {
    return sample.attachDocuments?.filter(x => x.fileStatus === FileStatus.Archived
      || x.fileStatus === FileStatus.ArchivedDeleted)?.length;
  }

  getArchivedFilesMessage(sample: Sample): string {
    return StringFormatter.format(TranslationService.translate('vbeiDocumentsRemovedOver60DaysMessage'),
      this.getArchivedFilesCount(sample).toString());
  }

  private mapOrderAttachments(allFiles: File[]) {
    const sampleAttachments = allFiles?.filter(x => x.linkedObjectType === LinkedObjectType.Sample);
    if (sampleAttachments?.length > 0) {
      this.attachDocumentService.deleteAttachment(this.currentOrder.OrderReference,
        sampleAttachments.map(x => x.internalFileName)).subscribe();
    }
    this.currentOrder.attachDocuments =
      this.mapFileDtoToAttachments(allFiles?.filter(x => x.linkedObjectType === LinkedObjectType.Order));
  }

  private mapSampleAttachments(allFiles: File[]) {
    const sampleAttachments = allFiles?.filter(x => x.linkedObjectType === LinkedObjectType.Sample);
    const orderAttachments = allFiles?.filter(x => x.linkedObjectType === LinkedObjectType.Order);
    if (orderAttachments?.length > 0) {
      this.attachDocumentService.deleteAttachment(this.currentOrder.OrderReference,
        orderAttachments.map(x => x.internalFileName)).subscribe();
    }
    this.currentOrder.samples.forEach(sample => {
      sample.attachDocuments = this.mapFileDtoToAttachments(sampleAttachments?.filter(x => x.sampleCode === sample.eurofinsSampleCode));
    });
  }

  private mapFileDtoToAttachments(files: File[]): AttachDocument[] {
    const attachments: AttachDocument[] = [];

    files?.forEach(file => {
      const attachment = new AttachDocument();
      attachment.internalFileName = file.internalFileName;
      attachment.name = file.fileName;
      attachment.status = file.scanStatus;
      attachment.fileStatus = file.status;
      const ext = file.fileName?.match(/\.[0-9a-z]+$/i)[0];
      attachment.icon = this.attachDocumentHelperService.getIcon(ext);
      attachments.push(attachment);
    });

    return attachments;
  }
}
